import { RefObject, useEffect, useRef } from "react";

/**
 * Calls `handler` when a pointer event occurs outside the referenced element.
 * Generic, app-wide replacement for the hand-rolled "close on outside click"
 * effect duplicated across dropdowns/popovers.
 *
 * @param ref      element to treat as "inside"
 * @param handler  invoked on an outside click
 * @param enabled  skip the listener when false (e.g. while closed)
 * @param eventType "mousedown" (default), "click", or "pointerdown"
 * @param ignoreSelector CSS selector for elements that count as "inside" even
 *   though they live elsewhere in the DOM — e.g. a `react-datepicker` calendar
 *   rendered into a body-level portal.
 */
export function useClickOutside<T extends HTMLElement>(
  ref: RefObject<T | null>,
  handler: () => void,
  enabled = true,
  eventType: "mousedown" | "click" | "pointerdown" = "mousedown",
  ignoreSelector?: string,
) {
  // Keep the latest handler without re-subscribing every render.
  const savedHandler = useRef(handler);
  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!enabled) return;
    const listener = (event: Event) => {
      const el = ref.current;
      const target = event.target as Node;
      if (!el || el.contains(target)) return;
      if (
        ignoreSelector &&
        target instanceof Element &&
        target.closest(ignoreSelector)
      ) {
        return;
      }
      savedHandler.current();
    };
    document.addEventListener(eventType, listener);
    return () => document.removeEventListener(eventType, listener);
  }, [ref, enabled, eventType, ignoreSelector]);
}
